// src/pages/finance-portal/settings/ARSettings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Switch, Tabs, Tab,
    FormControlLabel, Checkbox, MenuItem, Select, InputAdornment,
    IconButton, Alert, CircularProgress, FormControl, InputLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CalculateIcon from '@mui/icons-material/Calculate';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const cardSx = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    mb: 3,
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function getDaysDescription(days) {
    const n = Number(days);
    if (n < 0) return `${Math.abs(n)} day${Math.abs(n) !== 1 ? 's' : ''} before due`;
    if (n > 0) return `${n} day${n !== 1 ? 's' : ''} after due`;
    return 'On due date';
}

export default function ARSettings() {
    const { enqueueSnackbar } = useSnackbar();
    const [tab, setTab] = useState(0);

    // -- Late Fees State --
    const [lfLoading, setLfLoading] = useState(true);
    const [lfSaving, setLfSaving] = useState(false);
    const [lfCalculating, setLfCalculating] = useState(false);
    const [lfCalcResult, setLfCalcResult] = useState(null);
    const [lateFeeConfig, setLateFeeConfig] = useState({
        fee_type: 'percentage',
        fee_value: '',
        grace_period_days: '',
        is_compounding: false,
        compounding_period: 'monthly',
        cap_per_invoice: '',
        cap_annual: '',
        is_active: true,
    });

    // -- Payment Reminders State --
    const [prLoading, setPrLoading] = useState(true);
    const [prSaving, setPrSaving] = useState(false);
    const [prSending, setPrSending] = useState(false);
    const [prSendResult, setPrSendResult] = useState(null);
    const [reminderConfig, setReminderConfig] = useState({
        is_active: true,
        sequence: [],
    });

    // -- Fetch Late Fees Config --
    const fetchLateFeeConfig = useCallback(async () => {
        setLfLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/late-fees/config');
            const cfg = res.data?.data || res.data || {};
            setLateFeeConfig({
                fee_type: cfg.fee_type || 'percentage',
                fee_value: cfg.fee_value ?? '',
                grace_period_days: cfg.grace_period_days ?? '',
                is_compounding: cfg.is_compounding || false,
                compounding_period: cfg.compounding_period || 'monthly',
                cap_per_invoice: cfg.cap_per_invoice ?? '',
                cap_annual: cfg.cap_annual ?? '',
                is_active: cfg.is_active !== undefined ? cfg.is_active : true,
            });
        } catch {
            enqueueSnackbar('Failed to load late fee settings.', { variant: 'error' });
        } finally {
            setLfLoading(false);
        }
    }, [enqueueSnackbar]);

    // -- Fetch Payment Reminders Config --
    const fetchReminderConfig = useCallback(async () => {
        setPrLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/payment-reminders/config');
            const cfg = res.data?.data || res.data || {};
            setReminderConfig({
                is_active: cfg.is_active !== undefined ? cfg.is_active : true,
                sequence: Array.isArray(cfg.sequence) ? cfg.sequence : [],
            });
        } catch {
            enqueueSnackbar('Failed to load reminder settings.', { variant: 'error' });
        } finally {
            setPrLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        fetchLateFeeConfig();
        fetchReminderConfig();
    }, [fetchLateFeeConfig, fetchReminderConfig]);

    // -- Late Fees Handlers --
    const handleLfChange = (field, value) => {
        setLateFeeConfig((prev) => ({ ...prev, [field]: value }));
    };

    const saveLateFeesConfig = async () => {
        setLfSaving(true);
        try {
            await axiosInstance.put('/api/finance/late-fees/config', lateFeeConfig);
            enqueueSnackbar('Late fee settings saved.', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save late fee settings.', { variant: 'error' });
        } finally {
            setLfSaving(false);
        }
    };

    const calculateNow = async () => {
        setLfCalculating(true);
        setLfCalcResult(null);
        try {
            const res = await axiosInstance.post('/api/finance/late-fees/calculate', {});
            const result = res.data?.data || res.data;
            setLfCalcResult(result);
            enqueueSnackbar('Late fees calculated successfully.', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to calculate late fees.', { variant: 'error' });
        } finally {
            setLfCalculating(false);
        }
    };

    // -- Payment Reminders Handlers --
    const handleReminderActiveChange = (val) => {
        setReminderConfig((prev) => ({ ...prev, is_active: val }));
    };

    const addStep = () => {
        setReminderConfig((prev) => ({
            ...prev,
            sequence: [...prev.sequence, { days_offset: -7, channel: 'email' }],
        }));
    };

    const updateStep = (index, field, value) => {
        setReminderConfig((prev) => {
            const seq = [...prev.sequence];
            seq[index] = { ...seq[index], [field]: value };
            return { ...prev, sequence: seq };
        });
    };

    const removeStep = (index) => {
        setReminderConfig((prev) => ({
            ...prev,
            sequence: prev.sequence.filter((_, i) => i !== index),
        }));
    };

    const moveStep = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= reminderConfig.sequence.length) return;
        setReminderConfig((prev) => {
            const seq = [...prev.sequence];
            const temp = seq[index];
            seq[index] = seq[newIndex];
            seq[newIndex] = temp;
            return { ...prev, sequence: seq };
        });
    };

    const saveReminderConfig = async () => {
        setPrSaving(true);
        try {
            await axiosInstance.put('/api/finance/payment-reminders/config', reminderConfig);
            enqueueSnackbar('Reminder settings saved.', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save reminder settings.', { variant: 'error' });
        } finally {
            setPrSaving(false);
        }
    };

    const sendNow = async () => {
        setPrSending(true);
        setPrSendResult(null);
        try {
            const res = await axiosInstance.post('/api/finance/payment-reminders/send', {});
            const result = res.data?.data || res.data;
            setPrSendResult(result);
            enqueueSnackbar('Reminders sent successfully.', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to send reminders.', { variant: 'error' });
        } finally {
            setPrSending(false);
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SettingsIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700 }}>
                            AR Settings
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', opacity: 0.9 }}>
                            Configure late fees and payment reminder rules
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label="Late Fees" />
                    <Tab label="Payment Reminders" />
                </Tabs>
            </Paper>

            {/* Tab 1: Late Fees */}
            <TabPanel value={tab} index={0}>
                {lfLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                ) : (
                    <>
                        <Paper elevation={0} sx={cardSx}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>
                                Late Fee Configuration
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={lateFeeConfig.is_active}
                                            onChange={(e) => handleLfChange('is_active', e.target.checked)}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontFamily, fontWeight: 600 }}>
                                            {lateFeeConfig.is_active ? 'Active' : 'Inactive'}
                                        </Typography>
                                    }
                                />
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                                <FormControl size="small" sx={inputSx}>
                                    <InputLabel sx={{ fontFamily }}>Fee Type</InputLabel>
                                    <Select
                                        value={lateFeeConfig.fee_type}
                                        label="Fee Type"
                                        onChange={(e) => handleLfChange('fee_type', e.target.value)}
                                        sx={{ fontFamily, borderRadius: '10px' }}
                                    >
                                        <MenuItem value="percentage" sx={{ fontFamily }}>Percentage</MenuItem>
                                        <MenuItem value="fixed" sx={{ fontFamily }}>Fixed Amount</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Fee Value"
                                    size="small"
                                    type="number"
                                    value={lateFeeConfig.fee_value}
                                    onChange={(e) => handleLfChange('fee_value', e.target.value)}
                                    InputProps={{
                                        startAdornment: lateFeeConfig.fee_type === 'fixed' ? (
                                            <InputAdornment position="start">$</InputAdornment>
                                        ) : null,
                                        endAdornment: lateFeeConfig.fee_type === 'percentage' ? (
                                            <InputAdornment position="end">%</InputAdornment>
                                        ) : null,
                                    }}
                                    sx={inputSx}
                                />

                                <TextField
                                    label="Grace Period (Days)"
                                    size="small"
                                    type="number"
                                    value={lateFeeConfig.grace_period_days}
                                    onChange={(e) => handleLfChange('grace_period_days', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={inputSx}
                                />
                            </Box>

                            <Box sx={{ mb: 2.5 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={lateFeeConfig.is_compounding}
                                            onChange={(e) => handleLfChange('is_compounding', e.target.checked)}
                                            sx={{ '&.Mui-checked': { color: ACCENT } }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontFamily, fontWeight: 500 }}>
                                            Compounding
                                        </Typography>
                                    }
                                />
                            </Box>

                            {lateFeeConfig.is_compounding && (
                                <Box sx={{ mb: 2.5 }}>
                                    <FormControl size="small" sx={{ ...inputSx, minWidth: 200 }}>
                                        <InputLabel sx={{ fontFamily }}>Compounding Period</InputLabel>
                                        <Select
                                            value={lateFeeConfig.compounding_period}
                                            label="Compounding Period"
                                            onChange={(e) => handleLfChange('compounding_period', e.target.value)}
                                            sx={{ fontFamily, borderRadius: '10px' }}
                                        >
                                            <MenuItem value="monthly" sx={{ fontFamily }}>Monthly</MenuItem>
                                            <MenuItem value="daily" sx={{ fontFamily }}>Daily</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 3 }}>
                                <TextField
                                    label="Cap Per Invoice (optional)"
                                    size="small"
                                    type="number"
                                    value={lateFeeConfig.cap_per_invoice}
                                    onChange={(e) => handleLfChange('cap_per_invoice', e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                    sx={inputSx}
                                />
                                <TextField
                                    label="Cap Annual (optional)"
                                    size="small"
                                    type="number"
                                    value={lateFeeConfig.cap_annual}
                                    onChange={(e) => handleLfChange('cap_annual', e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                    sx={inputSx}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    onClick={saveLateFeesConfig}
                                    disabled={lfSaving}
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        bgcolor: ACCENT,
                                        '&:hover': { bgcolor: '#16a34a' },
                                        px: 4,
                                    }}
                                >
                                    {lfSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Settings'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={calculateNow}
                                    disabled={lfCalculating}
                                    startIcon={lfCalculating ? <CircularProgress size={18} /> : <CalculateIcon />}
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        borderColor: ACCENT,
                                        color: ACCENT,
                                        '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                                        px: 3,
                                    }}
                                >
                                    Calculate Now
                                </Button>
                            </Box>
                        </Paper>

                        {lfCalcResult && (
                            <Paper elevation={0} sx={cardSx}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 1.5, color: '#1e293b' }}>
                                    Calculation Result
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f0fdf4' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                            Invoices Processed
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                                            {lfCalcResult.invoices_processed ?? lfCalcResult.processed ?? 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#fef9c3' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                            Fees Applied
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                                            {lfCalcResult.fees_applied ?? lfCalcResult.applied ?? 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#fecaca' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                            Total Fees
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                                            ${Number(lfCalcResult.total_fees ?? lfCalcResult.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}
                    </>
                )}
            </TabPanel>

            {/* Tab 2: Payment Reminders */}
            <TabPanel value={tab} index={1}>
                {prLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                ) : (
                    <>
                        <Paper elevation={0} sx={cardSx}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', mb: 2.5, color: '#1e293b' }}>
                                Payment Reminder Configuration
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={reminderConfig.is_active}
                                            onChange={(e) => handleReminderActiveChange(e.target.checked)}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontFamily, fontWeight: 600 }}>
                                            {reminderConfig.is_active ? 'Active' : 'Inactive'}
                                        </Typography>
                                    }
                                />
                            </Box>

                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', mb: 2, color: '#475569' }}>
                                Reminder Sequence
                            </Typography>

                            {reminderConfig.sequence.length === 0 && (
                                <Box
                                    sx={{
                                        py: 4,
                                        textAlign: 'center',
                                        borderRadius: '12px',
                                        border: '2px dashed #e2e8f0',
                                        mb: 2,
                                    }}
                                >
                                    <Typography sx={{ fontFamily, color: '#94a3b8', mb: 1 }}>
                                        No reminder steps configured yet.
                                    </Typography>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                        Add steps to define when and how reminders are sent.
                                    </Typography>
                                </Box>
                            )}

                            {reminderConfig.sequence.map((step, index) => (
                                <Paper
                                    key={index}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        mb: 1.5,
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        bgcolor: '#fafafa',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#94a3b8',
                                            fontSize: '0.85rem',
                                            minWidth: 30,
                                        }}
                                    >
                                        #{index + 1}
                                    </Typography>

                                    <TextField
                                        label="Days Offset"
                                        size="small"
                                        type="number"
                                        value={step.days_offset}
                                        onChange={(e) => updateStep(index, 'days_offset', Number(e.target.value))}
                                        sx={{ ...inputSx, width: 130 }}
                                    />

                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', minWidth: 140 }}>
                                        {getDaysDescription(step.days_offset)}
                                    </Typography>

                                    <FormControl size="small" sx={{ ...inputSx, minWidth: 130 }}>
                                        <InputLabel sx={{ fontFamily }}>Channel</InputLabel>
                                        <Select
                                            value={step.channel || 'email'}
                                            label="Channel"
                                            onChange={(e) => updateStep(index, 'channel', e.target.value)}
                                            sx={{ fontFamily, borderRadius: '10px' }}
                                        >
                                            <MenuItem value="email" sx={{ fontFamily }}>Email</MenuItem>
                                            <MenuItem value="sms" sx={{ fontFamily }}>SMS</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            disabled={index === 0}
                                            onClick={() => moveStep(index, -1)}
                                            sx={{ color: '#64748b' }}
                                        >
                                            <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={index === reminderConfig.sequence.length - 1}
                                            onClick={() => moveStep(index, 1)}
                                            sx={{ color: '#64748b' }}
                                        >
                                            <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeStep(index)}
                                            sx={{ color: '#ef4444' }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            ))}

                            <Button
                                startIcon={<AddIcon />}
                                onClick={addStep}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    color: ACCENT,
                                    mt: 1,
                                    mb: 3,
                                    '&:hover': { bgcolor: '#f0fdf4' },
                                }}
                            >
                                Add Step
                            </Button>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    onClick={saveReminderConfig}
                                    disabled={prSaving}
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        bgcolor: ACCENT,
                                        '&:hover': { bgcolor: '#16a34a' },
                                        px: 4,
                                    }}
                                >
                                    {prSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Settings'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={sendNow}
                                    disabled={prSending}
                                    startIcon={prSending ? <CircularProgress size={18} /> : <SendIcon />}
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        borderColor: ACCENT,
                                        color: ACCENT,
                                        '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                                        px: 3,
                                    }}
                                >
                                    Send Now
                                </Button>
                            </Box>
                        </Paper>

                        {prSendResult && (
                            <Paper elevation={0} sx={cardSx}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 1.5, color: '#1e293b' }}>
                                    Send Result
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f0fdf4' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                            Reminders Sent
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                                            {prSendResult.sent ?? prSendResult.reminders_sent ?? 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#dbeafe' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                            Customers Notified
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                                            {prSendResult.customers_notified ?? prSendResult.customers ?? 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#fecaca' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                            Failed
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.3rem', fontWeight: 700, color: '#1e293b' }}>
                                            {prSendResult.failed ?? 0}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}
                    </>
                )}
            </TabPanel>
        </Box>
    );
}
